/**
 * Velocity Verlet Numerical Physics Engine (Fixed 120Hz Tick)
 * Solves second-order differential mass-spring-damper dynamics.
 */

import { MemoryPool } from '../core/MemoryPool';

export interface PhysicsMaterial {
  mass: number;
  stiffness: number; // spring constant k
  damping: number;   // damping constant c
  friction: number;  // surface friction coefficient
}

export const MATERIALS: Record<string, PhysicsMaterial> = {
  STONE:  { mass: 50.0, stiffness: 1200, damping: 85.0, friction: 0.8 },
  BRONZE: { mass: 15.0, stiffness: 2200, damping: 45.0, friction: 0.4 },
  IRON:   { mass: 5.0,  stiffness: 3500, damping: 30.0, friction: 0.3 },
  LIGHT:  { mass: 0.1,  stiffness: 8000, damping: 5.0,  friction: 0.05 }
};

export class VerletKernel {
  public static readonly FIXED_DELTA = 1 / 120; // 0.0083333s

  private memoryPool: MemoryPool;

  constructor() {
    this.memoryPool = MemoryPool.getInstance();
  }

  /**
   * Updates particle positions and velocities using Velocity Verlet
   */
  public updateParticles(dt: number = VerletKernel.FIXED_DELTA): void {
    const buf = this.memoryPool.particleBuffer;
    const count = this.memoryPool.activeParticleCount;
    const stride = MemoryPool.FLOATS_PER_PARTICLE;

    let writeIdx = 0;

    for (let i = 0; i < count; i++) {
      const idx = i * stride;

      let x = buf[idx + 0];
      let y = buf[idx + 1];
      let z = buf[idx + 2];
      let vx = buf[idx + 3];
      let vy = buf[idx + 4];
      let vz = buf[idx + 5];
      let life = buf[idx + 6];
      const maxLife = buf[idx + 7];
      const size = buf[idx + 8];
      const r = buf[idx + 9];
      const g = buf[idx + 10];
      const b = buf[idx + 11];
      let a = buf[idx + 12];

      life -= dt;

      if (life > 0) {
        // Air resistance damping & gravity
        const drag = 0.96;
        const gravity = -1.5;

        vx *= drag;
        vy = vy * drag + gravity * dt;
        vz *= drag;

        x += vx * dt;
        y += vy * dt;
        z += vz * dt;

        // Fade alpha based on remaining life
        a = (life / maxLife);

        // Copy alive particle to write index (in-place pool compression)
        const wIdx = writeIdx * stride;
        buf[wIdx + 0] = x;
        buf[wIdx + 1] = y;
        buf[wIdx + 2] = z;
        buf[wIdx + 3] = vx;
        buf[wIdx + 4] = vy;
        buf[wIdx + 5] = vz;
        buf[wIdx + 6] = life;
        buf[wIdx + 7] = maxLife;
        buf[wIdx + 8] = size;
        buf[wIdx + 9] = r;
        buf[wIdx + 10] = g;
        buf[wIdx + 11] = b;
        buf[wIdx + 12] = a;

        writeIdx++;
      }
    }

    this.memoryPool.activeParticleCount = writeIdx;
  }
}
